import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Ban } from "lucide-react";

const STATUS_BADGE = {
  waiting: { bg: "bg-slate-100", color: "text-slate-700", label: "En attente" },
  called: { bg: "bg-green-100", color: "text-green-700", label: "Appelé" },
  serving: { bg: "bg-blue-100", color: "text-blue-700", label: "En service" },
  completed: { bg: "bg-emerald-100", color: "text-emerald-700", label: "Terminé" },
  missed: { bg: "bg-red-100", color: "text-red-700", label: "Manqué" },
  transferred: { bg: "bg-indigo-100", color: "text-indigo-700", label: "Transféré" },
  cancelled: { bg: "bg-slate-100", color: "text-slate-500", label: "Annulé" },
};

export default function HistoryPage() {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [filters, setFilters] = useState({ agency_id: "all", status: "all", date_str: "" });
  const [tickets, setTickets] = useState([]);
  const [cancelTicket, setCancelTicket] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const canCancel = ["SUPER_ADMIN", "AGENCY_ADMIN", "RECEPTIONIST"].includes(user?.role);

  useEffect(() => { api.get("/agencies").then((r) => setAgencies(r.data)); }, []);

  const refresh = useCallback(async () => {
    const params = {};
    if (filters.agency_id !== "all") params.agency_id = filters.agency_id;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.date_str) params.date_str = filters.date_str;
    params.limit = 500;
    const { data } = await api.get("/tickets", { params });
    setTickets(data);
  }, [filters]);
  useEffect(() => { refresh(); }, [refresh]);

  const downloadCsv = async () => {
    const params = new URLSearchParams();
    if (filters.agency_id !== "all") params.set("agency_id", filters.agency_id);
    if (filters.date_str) params.set("date_str", filters.date_str);
    const res = await api.get(`/export/tickets.csv?${params}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `tickets.csv`; a.click();
  };

  const doCancel = async () => {
    if (!cancelTicket || !cancelReason.trim() || cancelReason.trim().length < 3) {
      toast.error("Motif requis (min. 3 caractères)");
      return;
    }
    try {
      await api.post(`/tickets/${cancelTicket.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(`Ticket ${cancelTicket.full_ticket_code} annulé`);
      setCancelTicket(null);
      setCancelReason("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-6" data-testid="history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl">Historique des tickets</h1>
          <p className="text-slate-500 text-sm mt-1">Consultez et exportez tous les tickets émis.</p>
        </div>
        <Button onClick={downloadCsv} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="history-export">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3">
        {(user?.role === "SUPER_ADMIN" || user?.role === "AUDITOR") && (
          <Select value={filters.agency_id} onValueChange={(v) => setFilters({ ...filters, agency_id: v })}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes agences</SelectItem>
              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(STATUS_BADGE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={filters.date_str} onChange={(e) => setFilters({ ...filters, date_str: e.target.value })} className="w-44" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left p-3">Numéro</th><th className="text-left">Catégorie</th>
              <th className="text-left">Agence</th><th className="text-left">Guichet</th>
              <th className="text-left">Agent</th><th>Statut</th>
              <th className="text-left">Créé</th><th className="text-left">Terminé</th>
              {canCancel && <th className="text-right pr-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => {
              const b = STATUS_BADGE[t.status] || STATUS_BADGE.waiting;
              return (
                <tr key={t.id} className="border-t border-slate-200 hover:bg-slate-50/50">
                  <td className="p-3 font-mono font-bold text-slate-900">{t.full_ticket_code}</td>
                  <td><span style={{ color: t.color }} className="font-semibold">{t.category_code}</span> <span className="text-slate-500 text-xs">{t.category_label}</span></td>
                  <td className="text-slate-600 text-xs">{t.agency_code}</td>
                  <td className="text-slate-600 text-xs">{t.assigned_counter_name || "—"}</td>
                  <td className="text-slate-600 text-xs">{t.assigned_agent_name || "—"}</td>
                  <td className="text-center"><span className={`text-xs px-2 py-0.5 rounded ${b.bg} ${b.color}`}>{b.label}</span></td>
                  <td className="text-slate-500 text-xs font-mono">{new Date(t.created_at).toLocaleString("fr-FR")}</td>
                  <td className="text-slate-500 text-xs font-mono">{t.completed_at ? new Date(t.completed_at).toLocaleTimeString("fr-FR") : "—"}</td>
                  {canCancel && (
                    <td className="text-right pr-2">
                      {["waiting", "called", "serving", "missed"].includes(t.status) ? (
                        <Button size="sm" variant="ghost"
                                data-testid={`cancel-ticket-${t.full_ticket_code}`}
                                onClick={() => setCancelTicket(t)}>
                          <Ban className="w-3.5 h-3.5 text-[#DC2626]" />
                        </Button>
                      ) : null}
                    </td>
                  )}
                </tr>
              );
            })}
            {tickets.length === 0 && <tr><td colSpan={canCancel ? 9 : 8} className="text-center text-slate-400 py-12">Aucun ticket</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!cancelTicket} onOpenChange={(v) => { if (!v) { setCancelTicket(null); setCancelReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuler le ticket {cancelTicket?.full_ticket_code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Motif d'annulation (obligatoire)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ex: client absent, doublon, erreur de catégorie…"
              rows={3}
              data-testid="cancel-reason-input"
              minLength={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTicket(null)}>Retour</Button>
            <Button onClick={doCancel} disabled={!cancelReason.trim() || cancelReason.trim().length < 3}
                    className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="cancel-confirm">
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
